import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Star, Bell, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import UnitSelector from './shared/UnitSelector';
import { SEVERITY_STYLES } from './shared/alertStyles';
import { formatRelativeTime } from '../utils/formatters';

function Header({ pageTitle, showMenuButton = false, onMenuToggle, menuButtonRef, notificationCount = 0, headerAlerts = [], currentRole }) {
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handlePopOut = () => {
    const url = location.pathname + location.search;
    window.open(url, '_blank', 'noopener');
  };

  useEffect(() => {
    if (!showNotifications) return;
    const handleOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [showNotifications]);

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="header-title-wrap">
          {showMenuButton && (
            <Button
              ref={menuButtonRef}
              type="button"
              variant="ghost"
              size="icon"
              onClick={onMenuToggle}
              aria-label="Open sidebar menu"
              title="Open sidebar"
              className="text-text-primary hover:text-white hover:bg-white/10"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </Button>
          )}
          <div className="header-title">{pageTitle || 'Clinical Operations Command Center'}</div>
        </div>

        <div className="header-actions flex items-center gap-2">
          {currentRole === 'manager' && <UnitSelector variant="header" />}

          <Link
            to="/favorites"
            aria-label="Favourites"
            title="Favourites"
            className="header-action-btn flex items-center text-text-primary hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <Star className="w-5 h-5" />
          </Link>

          <div ref={notifRef} className="relative">
            <Button
              data-testid="notifications-btn"
              variant="ghost"
              size="icon"
              onClick={() => setShowNotifications(v => !v)}
              aria-label="Notifications"
              title="Notifications"
              className="relative text-text-primary hover:text-white hover:bg-white/10"
            >
              <Bell className="w-5 h-5" />
              {notificationCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-red-critical text-white rounded-full text-[10px] font-bold flex items-center justify-center px-1">
                  {notificationCount}
                </span>
              )}
            </Button>

            {showNotifications && (
              <div className="absolute top-[calc(100%+8px)] right-0 w-[360px] bg-nn-white border border-nn-border rounded-card shadow-md z-tooltip overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-nn-border">
                  <p className="text-body font-semibold text-text-dark">Operational Alerts</p>
                  {notificationCount > 0 && (
                    <span className="text-[10px] font-bold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded">
                      {notificationCount} active
                    </span>
                  )}
                </div>

                {headerAlerts.length === 0 ? (
                  <p className="text-body-sm text-text-dark-secondary text-center py-6">
                    No active alerts
                  </p>
                ) : (
                  <div className="max-h-[320px] overflow-y-auto">
                    {headerAlerts.slice(0, 10).map((alert) => {
                      const sev = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.medium;
                      return (
                        <button
                          key={alert.id ?? alert.alertId}
                          onClick={() => { setShowNotifications(false); navigate(`/alerts?expand=${alert.id ?? alert.alertId}`); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-surface-warm transition-colors flex items-start gap-2.5 border-b border-nn-border/50 last:border-0"
                        >
                          <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${sev.dot}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className={`text-[9px] font-semibold px-1 py-0.5 rounded ${sev.badge}`}>{sev.label}</span>
                              <span className="text-[10px] text-text-dark-secondary truncate">{alert.triggerEventType}</span>
                            </div>
                            <p className="text-body-sm text-text-dark mt-0.5 line-clamp-2">{alert.summary}</p>
                            {alert.createdAt && (
                              <span className="text-[10px] text-text-dark-secondary">{formatRelativeTime(alert.createdAt)}</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                <Link
                  to="/alerts"
                  onClick={() => setShowNotifications(false)}
                  className="block text-center text-body-sm font-medium text-aqua hover:text-aqua/80 py-2.5 border-t border-nn-border"
                >
                  View all alerts
                </Link>
              </div>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={handlePopOut}
            aria-label="Open in new window"
            title="Open in new window"
            className="ml-auto text-text-primary hover:text-white hover:bg-white/10"
          >
            <ExternalLink className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  );
}

export default Header;
